<template>
  <div class="upload-breakdown-container">
    <div class="upload-breakdown-header mb-2">
      <v-icon small class="mr-1">analytics</v-icon>
      <span class="text-subtitle-2 font-weight-medium"
        >Upload Statistics</span
      >
    </div>
    <div class="upload-breakdown-grid">
      <template v-for="(data, idx) in dataPoints">
        <div v-if="data.spacer" :key="idx" class="grid-spacer"></div>
        <upload-breakdown-item
          v-else
          :key="idx"
          :icon="data.icon"
          :color="data.color"
          :label="data.suffix"
          :value="data.count"
          :tooltip="data.tooltip"
          class="breakdown-item"
        />
      </template>
    </div>
    <div
      v-if="uploadsInProgress"
      class="upload-breakdown-updating text-caption mt-2"
    >
      <v-icon x-small class="mr-1">sync</v-icon>
      Counts update as related uploads finish processing.
    </div>
  </div>
</template>

<script>
import UploadBreakdownItem from '@/components/UploadsPage/UploadBreakdownItem';
import { ReferenceFile } from '@/models';

const DATA_POINTS = [
  {
    icon: 'summarize',
    color: 'indigo lighten-1',
    value: (referenceFile) =>
      (referenceFile.recordsParsedCount || 0) +
      (referenceFile.invalidCount || 0),
    suffix: 'Total (file)',
    tooltip: 'Total records in file (valid + invalid)',
  },
  {
    icon: 'description',
    color: 'blue lighten-1',
    value: (referenceFile) => referenceFile.recordsParsedCount || 0,
    suffix: 'Valid',
    tooltip:
      'Valid records parsed from the uploaded file. Records deleted after upload are no longer reflected in the tiles below.',
  },
  {
    icon: 'error_outline',
    color: 'error lighten-1',
    value: (referenceFile) => referenceFile.invalidCount || 0,
    suffix: 'Invalid',
    tooltip:
      'Records that could not be parsed or were missing required fields',
  },
  // Two spacers pad the file-stats row out to the full five columns, so the
  // five tiles that reconcile to Valid sit together on the row below.
  {
    spacer: true,
  },
  {
    spacer: true,
  },
  {
    icon: 'add_circle',
    color: 'success',
    value: (referenceFile) => referenceFile.newReferenceCount || 0,
    suffix: 'New',
    tooltip: 'New unique references added to the review',
  },
  {
    icon: 'check_circle',
    color: 'teal',
    value: (referenceFile) =>
      referenceFile.autoResolvedDuplicateCount || 0,
    suffix: 'Auto-resolved',
    tooltip:
      'Duplicates automatically resolved by DOI/title/abstract matching',
  },
  {
    icon: 'search',
    color: 'orange darken-1',
    value: (referenceFile) =>
      referenceFile.potentialDuplicateCount || 0,
    suffix: 'Review needed',
    tooltip: 'Potential duplicates requiring manual review',
  },
  {
    icon: 'how_to_reg',
    color: 'blue-grey',
    value: (referenceFile) => referenceFile.manualDuplicateCount || 0,
    suffix: 'Manual',
    tooltip: 'Duplicates marked by a reviewer',
  },
  // Last of the five: unlike the others, these rows were never inserted into
  // the review at all — they are counted on the file rather than derived live.
  {
    icon: 'content_copy',
    color: 'purple lighten-1',
    value: (referenceFile) =>
      referenceFile.identicalDuplicateCount || 0,
    suffix: 'Identical',
    tooltip:
      'Exact duplicates already in the review or repeated within this file',
  },
];

export default {
  name: 'UploadBreakdown',
  components: { UploadBreakdownItem },
  props: {
    referenceFile: {
      type: ReferenceFile,
      required: true,
    },
    uploadsInProgress: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dataPoints() {
      return DATA_POINTS.map((dataPoint) => {
        if (dataPoint.spacer) {
          return { spacer: true };
        }
        // `value` is the count accessor; the item renders `count` + `label`,
        // so keep only those and drop the accessor from the passed object.
        const { value, ...rest } = dataPoint;
        return {
          ...rest,
          count: value ? value(this.referenceFile) : 0,
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.upload-breakdown-container {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border-radius: 12px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);

  .upload-breakdown-header {
    display: flex;
    align-items: center;
    color: map-get($grey, 'darken-2');
    padding-left: 4px;
  }

  .upload-breakdown-grid {
    display: grid;
    // Five columns so the five tiles that reconcile to Valid (New,
    // Auto-resolved, Review needed, Manual, Identical) occupy exactly one row
    // rather than leaving a lone tile stranded on a third row.
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 960px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }

    .grid-spacer {
      @media (max-width: 960px) {
        display: none;
      }
    }

    .breakdown-item {
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }
}
</style>
